import { GetStaticProps, GetStaticPaths } from 'next'
import NextError from 'next/error'
import { contentful } from 'utils/contentful'
import Meta from 'components/Meta'
import { Header } from 'components/marketing/Header'
import { BlockRenderer } from 'components/contentful/BlockRenderer'
import { ThemeColorProvider } from 'components/contentful/ThemeContext'
import { BottomCTASection } from 'components/marketing/BottomCTASection'

export default function MarketingPage({ page }) {
  if (!page) {
    return <NextError statusCode={404} />
  }

  return (
    <div className='bg-grey'>
      <Meta
        title={page.title}
        description={page.metaDescription}
        imageSrc={page.metaImage?.url}
        imageHeight={page.metaImage?.height}
        imageWidth={page.metaImage?.width}
      />

      <Header />

      <main className='marketing-page'>
        <ThemeColorProvider themeColor={page.themeColor}>
          <BlockRenderer block={page.blocksCollection.items} />
        </ThemeColorProvider>

        <BottomCTASection
          title='Want to see how it works?'
          subline='Stellate helps companies reduce their infrastructure costs by up to 40%, eliminate downtime, and improve performance.'
        />
      </main>
    </div>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = Array.isArray(params!['marketing-slug'])
    ? params!['marketing-slug'].join('/')
    : params!['marketing-slug']

  try {
    const json = await contentful({
      query: /* GraphQL */ `
        fragment AssetFragment on Asset {
          url
          width
          height
          title
          __typename
        }

        query getMarketingPage($slug: String!) {
          marketingPageCollection(where: { slug: $slug }, limit: 1) {
            items {
              __typename
              title
              slug
              metaDescription
              metaImage {
                ...AssetFragment
              }
              themeColor
              blocksCollection(limit: 20) {
                __typename
                items {
                  __typename
                  ... on Entry {
                    __typename
                    sys {
                      __typename
                      id
                    }
                  }
                  ... on ComponentHeroCentered {
                    __typename
                    headline
                    subheadline
                    icon {
                      ...AssetFragment
                    }
                    badge
                    primaryCtaLabel
                    primaryCtaUrl
                    secondaryCtaLabel
                    secondaryCtaUrl
                    secondaryCtaIcon {
                      ...AssetFragment
                    }
                    fullWidthImage {
                      ...AssetFragment
                    }
                    videoUrl
                  }
                  ... on ComponentFeatures2X2Grid {
                    __typename
                    headline
                    subheadline
                    label
                    featuresCollection(limit: 10) {
                      __typename
                      items {
                        __typename
                        title
                        text
                        icon {
                          ...AssetFragment
                        }
                      }
                    }
                  }
                  ... on ComponentFeaturesWithImagesAlternatingSide {
                    __typename
                    headline
                    subheadline
                    label
                    featuresCollection(limit: 10) {
                      __typename
                      items {
                        title
                        text
                        icon {
                          ...AssetFragment
                        }
                        image {
                          __typename
                          title
                          url
                          height
                          width
                        }
                        linkLabel
                        linkUrl
                      }
                    }
                  }
                  ... on ComponentFeaturesCards {
                    __typename
                    headline
                    subheadline
                    label
                    cardsTitle
                    ctaLabel
                    ctaUrl
                    featuresCollection(limit: 10) {
                      __typename
                      items {
                        __typename
                        title
                        text
                        image {
                          ...AssetFragment
                        }
                        linkLabel
                        linkUrl
                      }
                    }
                  }
                  ... on ComponentVisualsSideBySide {
                    __typename
                    headline
                    subheadline
                    label
                    visual1 {
                      __typename
                      title
                      description
                      tooltip
                      text {
                        __typename
                        json
                      }
                      mobileImage {
                        ...AssetFragment
                      }
                      desktopImage {
                        ...AssetFragment
                      }
                    }
                    visual2 {
                      __typename
                      title
                      description
                      tooltip
                      text {
                        json
                      }
                      mobileImage {
                        ...AssetFragment
                      }
                      desktopImage {
                        ...AssetFragment
                      }
                    }
                  }
                  ... on ComponentTabsWithCode {
                    __typename
                    headline
                    subheadline
                    label
                    windowTitle
                    externalLinkLabel
                    externalLinkUrl
                    tabsCodeCollection(limit: 10) {
                      __typename
                      items {
                        __typename
                        title
                        text {
                          __typename
                          json
                        }
                        description
                        badge
                        codeBlock {
                          __typename
                          code
                          language
                        }
                      }
                    }
                  }
                  ... on ComponentImageOverlappingBackground {
                    __typename
                    headline
                    subheadline
                    label
                    linkLabel
                    linkUrl
                    image {
                      ...AssetFragment
                    }
                  }
                  ... on ComponentGraphiQlOverlappingBackground {
                    __typename
                    headline
                    subheadline
                    label
                    linkLabel
                    linkUrl
                    graphiQl {
                      __typename
                      sys {
                        __typename
                        id
                      }
                      graphQlEndpoint
                      defaultQuery
                    }
                  }
                  ... on ComponentTestimonialCentered {
                    __typename
                    headline
                    quote {
                      __typename
                      text
                      author {
                        __typename
                        name
                        role
                        company {
                          __typename
                          name
                        }
                        twitterHandle
                        avatar {
                          ...AssetFragment
                        }
                      }
                    }
                  }
                  ... on ComponentRelatedFeature {
                    __typename
                    headline
                    subheadline
                    featureName
                  }
                  ... on ComponentHeadlineWithEmbed {
                    __typename
                    headline
                    subheadline
                    iframeUrl
                    iframeHeight
                    underHeadlineText
                    fullWidthImage {
                      ...AssetFragment
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables: {
        slug,
      },
    })

    if (json.errors && Array.isArray(json.errors) && json.errors.length > 0) {
      throw new Error(
        `Failed to fetch marketing page: ${json.errors[0].message}`,
      )
    }

    return {
      props: {
        page: json.data.marketingPageCollection.items[0] || null,
      },
      revalidate: 10, // In seconds
    }
  } catch (e) {
    console.log(e)
    throw new Error('Failed to fetch marketing page')
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const json = await contentful({
    query: `query getMarketingPagePaths {
        marketingPageCollection {
          items {
            slug
          }
        }
      }
      `,
  })

  const paths = json.data.marketingPageCollection.items.map(
    (marketingPage) => ({
      params: { 'marketing-slug': marketingPage.slug.split('/') },
    }),
  )

  return { paths, fallback: 'blocking' }
}
